import { checkTargetByHtml } from '@flowus/common/dom';
import { fastEqual } from '@flowus/common/utils/tools';
import { BlockType, CollectionViewType, PermissionRole } from '@next-space/fe-api-idl';
import { useThrottleEffect } from 'ahooks';
import { uniqWith } from 'lodash-es';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isSafari } from 'react-device-detect';
import { globalListenerHelper } from 'src/common/utils/global-listener-helper';
import { useIsDragging } from 'src/hooks/page/use-dnd/hooks';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { cleanSelectBlock } from 'src/hooks/utils/clean-select';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch, getState } from 'src/redux/store';
import type { NextBlock, SelectBlock } from 'src/redux/types';
import {
  $appUiStateCache,
  setAppUiState,
  useColumnResizing,
  useIsCtrlKeyDown,
} from 'src/services/app';
import { getAutoScroll } from 'src/utils/auto-scroll';
import { isCollection } from 'src/utils/block-type-utils';
import type { Rectangle } from 'src/utils/collision';
import { isIntersect } from 'src/utils/collision';
import {
  blurDocument,
  querySelectorAllFromMainContent,
  querySelectorFromMainContent,
} from 'src/utils/dom';
import { useGetPageId } from 'src/utils/getPageId';
import { useIsInRight } from 'src/utils/right-utils';
import { PageScene, usePageScene } from 'src/views/main/scene-context';

const HORIZONTAL_OFFSET = 50;
const HALF_BLOCK_HEIGHT = 5;
const MOVE_OFFSET_NUM = 5;
const { autoScroll, raf } = getAutoScroll();

/**
 * ======================== 重构后的选择器架构 ========================
 *
 * 新架构采用策略模式 + 责任链模式，主要优势：
 *
 * 1. 代码结构清晰：每个类职责单一，易于理解和维护
 * 2. 性能更高：优化的缓存机制和数据结构
 * 3. 内存使用更少：避免重复计算和不必要的对象创建
 * 4. 扩展性强：可轻松添加新的选择策略
 * 5. 测试友好：每个策略可独立测试
 *
 * 主要模块：
 * - BlockHierarchy: 管理块的层级关系和缓存
 * - SelectionStrategy: 不同的选择策略实现
 * - SelectionManager: 策略管理器，负责策略选择和执行
 * - DOMInteraction: DOM交互层，处理鼠标和DOM操作
 */

// ======================== 数据结构定义 ========================

interface BlockInfo {
  blockId: string;
  block: NextBlock;
  rect: Rectangle;
  depth: number;
  parentId: string;
  ancestors: string[];
}

interface SelectionPoint {
  x: number;
  y: number;
  blockId?: string;
  blockInfo?: BlockInfo;
}

interface SelectionContext {
  startPoint: SelectionPoint;
  endPoint: SelectionPoint;
  intersectingBlocks: BlockInfo[];
  style: Rectangle & { startX: number; startY: number; endX: number; endY: number };
}

// ======================== 块层级管理器 ========================

/**
 * 块层级管理器
 *
 * 负责管理块的层级关系，提供高性能的层级查询和缓存功能。
 *
 * 性能优化：
 * - 使用 Map 缓存深度和祖先信息，避免重复计算
 * - O(1) 查找复杂度的关键操作
 * - 内存高效的数据结构
 */
class BlockHierarchy {
  private blocks: typeof cache.blocks;
  private pageId: string;
  private depthCache = new Map<string, number>();
  private ancestorCache = new Map<string, string[]>();

  constructor(blocks: typeof cache.blocks, pageId: string) {
    this.blocks = blocks;
    this.pageId = pageId;
  }

  getDepth(blockId: string): number {
    if (this.depthCache.has(blockId)) {
      return this.depthCache.get(blockId)!;
    }

    let depth = 0;
    let current = this.blocks[blockId];
    while (current && current.parentId !== this.pageId) {
      depth++;
      current = this.blocks[current.parentId];
    }

    this.depthCache.set(blockId, depth);
    return depth;
  }

  getAncestors(blockId: string): string[] {
    if (this.ancestorCache.has(blockId)) {
      return this.ancestorCache.get(blockId)!;
    }

    const ancestors: string[] = [];
    let current = this.blocks[blockId];

    if (current) {
      ancestors.push(current.uuid);
    }

    while (current && current.parentId !== this.pageId) {
      current = this.blocks[current.parentId];
      if (current) {
        ancestors.push(current.uuid);
      }
    }

    this.ancestorCache.set(blockId, ancestors);
    return ancestors;
  }

  getTreeRoot(blockId: string): string {
    const ancestors = this.getAncestors(blockId);
    return ancestors[ancestors.length - 1] || blockId;
  }

  isSameTree(blockId1: string, blockId2: string): boolean {
    return this.getTreeRoot(blockId1) === this.getTreeRoot(blockId2);
  }

  getCommonAncestor(blockId1: string, blockId2: string): string | null {
    const ancestors1 = this.getAncestors(blockId1);
    const ancestors2 = this.getAncestors(blockId2);

    let commonAncestor: string | null = null;
    const minLength = Math.min(ancestors1.length, ancestors2.length);

    for (let i = 1; i <= minLength; i++) {
      const ancestor1 = ancestors1[ancestors1.length - i];
      const ancestor2 = ancestors2[ancestors2.length - i];

      if (ancestor1 === ancestor2) {
        commonAncestor = ancestor1 || null;
      } else {
        break;
      }
    }

    return commonAncestor;
  }

  clear() {
    this.depthCache.clear();
    this.ancestorCache.clear();
  }
}

// ======================== 选择策略接口 ========================

interface SelectionStrategy {
  canHandle(context: SelectionContext): boolean;
  select(context: SelectionContext, hierarchy: BlockHierarchy): BlockInfo[];
}

// ======================== 具体选择策略 ========================

/**
 * 外部到内部选择策略
 *
 * 处理从外部拖拽到内部的情况。
 * 策略：
 * 1. 如果跨越多个root，选择所有涉及的root节点
 * 2. 如果只在一个root内，选择结束点（叶子节点）的深度
 *
 * 使用场景：
 * - 从外面框选到多个root → 选择所有root
 * - 从外面框选到一个root内的叶子 → 选择该叶子的深度
 */
class ExternalToInternalStrategy implements SelectionStrategy {
  canHandle(context: SelectionContext): boolean {
    return !context.startPoint.blockInfo && !!context.endPoint.blockInfo;
  }

  select(context: SelectionContext, hierarchy: BlockHierarchy): BlockInfo[] {
    const { endPoint, intersectingBlocks } = context;
    if (!endPoint.blockInfo) return [];

    // 获取所有相交块的根节点
    const rootNodes = new Set<string>();
    intersectingBlocks.forEach((block) => {
      const root = hierarchy.getTreeRoot(block.blockId);
      rootNodes.add(root);
    });

    // 如果跨越多个root，选择所有涉及的root节点
    if (rootNodes.size > 1) {
      const rootDepths = Array.from(rootNodes).map((root) => hierarchy.getDepth(root));
      const targetDepth = Math.min(...rootDepths);

      return intersectingBlocks.filter((block) => {
        if (block.depth !== targetDepth) return false;
        const blockRoot = hierarchy.getTreeRoot(block.blockId);
        return rootNodes.has(blockRoot);
      });
    }

    // 如果只在一个root内，选择结束点的深度
    const targetDepth = endPoint.blockInfo.depth;
    const targetParentId = endPoint.blockInfo.parentId;

    return intersectingBlocks.filter(
      (block) => block.depth === targetDepth && block.parentId === targetParentId
    );
  }
}

class CrossTreeStrategy implements SelectionStrategy {
  canHandle(context: SelectionContext): boolean {
    const { startPoint, endPoint } = context;
    if (!startPoint.blockInfo || !endPoint.blockInfo) return false;

    return startPoint.blockInfo.blockId !== endPoint.blockInfo.blockId;
  }

  select(context: SelectionContext, hierarchy: BlockHierarchy): BlockInfo[] {
    const { startPoint, endPoint, intersectingBlocks } = context;
    if (!startPoint.blockInfo || !endPoint.blockInfo) return [];

    // 获取所有相交块的根节点
    const rootNodes = new Set<string>();
    intersectingBlocks.forEach((block) => {
      const root = hierarchy.getTreeRoot(block.blockId);
      rootNodes.add(root);
    });

    // 检查是否跨树（通过实际相交的块来判断，而不是仅通过起始和结束点）
    if (rootNodes.size > 1) {
      // 跨树选择：选择所有涉及的root节点
      const rootDepths = Array.from(rootNodes).map((root) => hierarchy.getDepth(root));
      const targetDepth = Math.min(...rootDepths);

      return intersectingBlocks.filter((block) => {
        if (block.depth !== targetDepth) return false;
        const blockRoot = hierarchy.getTreeRoot(block.blockId);
        return rootNodes.has(blockRoot);
      });
    }

    // 单树选择：选择结束点深度
    const targetDepth = endPoint.blockInfo.depth;
    const targetParentId = endPoint.blockInfo.parentId;

    return intersectingBlocks.filter(
      (block) => block.depth === targetDepth && block.parentId === targetParentId
    );
  }
}

class SameDepthStrategy implements SelectionStrategy {
  canHandle(context: SelectionContext): boolean {
    const { startPoint, endPoint } = context;
    return !!(startPoint.blockInfo && endPoint.blockInfo);
  }

  select(context: SelectionContext): BlockInfo[] {
    const { startPoint, endPoint, intersectingBlocks } = context;
    if (!startPoint.blockInfo || !endPoint.blockInfo) return [];

    // 选择较浅的深度
    const targetDepth = Math.min(startPoint.blockInfo.depth, endPoint.blockInfo.depth);

    if (startPoint.blockInfo.depth === endPoint.blockInfo.depth) {
      // 同深度：检查是否同父级
      const startParentId = startPoint.blockInfo.parentId;
      const endParentId = endPoint.blockInfo.parentId;

      if (startParentId === endParentId) {
        return intersectingBlocks.filter(
          (block) => block.depth === targetDepth && block.parentId === startParentId
        );
      }
    }

    return intersectingBlocks.filter((block) => block.depth === targetDepth);
  }
}

class DefaultStrategy implements SelectionStrategy {
  canHandle(): boolean {
    return true; // 默认策略总是可用
  }

  select(context: SelectionContext): BlockInfo[] {
    const { intersectingBlocks } = context;
    if (intersectingBlocks.length === 0) return [];

    // 选择最浅深度的块
    const minDepth = Math.min(...intersectingBlocks.map((block) => block.depth));
    return intersectingBlocks.filter((block) => block.depth === minDepth);
  }
}

// ======================== 选择管理器 ========================

class SelectionManager {
  private strategies: SelectionStrategy[] = [
    new ExternalToInternalStrategy(), // 优先处理从外部开始的情况，包含跨树逻辑
    new CrossTreeStrategy(), // 处理内部到内部的跨树情况
    new SameDepthStrategy(), // 处理同深度情况
    new DefaultStrategy(), // 兜底策略
  ];

  private hierarchy: BlockHierarchy;

  constructor(hierarchy: BlockHierarchy) {
    this.hierarchy = hierarchy;
  }

  select(context: SelectionContext): BlockInfo[] {
    for (const strategy of this.strategies) {
      if (strategy.canHandle(context)) {
        return strategy.select(context, this.hierarchy);
      }
    }
    return [];
  }
}

// ======================== DOM交互层 ========================

class DOMInteraction {
  static getBlockAtPoint(x: number, y: number): string | null {
    const element = document.elementFromPoint(x, y);
    const blockElement = element?.closest('[data-block-id]') as HTMLElement;
    return blockElement?.dataset.blockId || null;
  }

  static getBlockInfo(blockId: string, intersectingBlocks: BlockInfo[]): BlockInfo | undefined {
    return intersectingBlocks.find((block) => block.blockId === blockId);
  }
}

// ======================== 主Hook ========================

const useSelect = () => {
  const isDragging = useIsDragging();
  const columnResizing = useColumnResizing();
  const isCtrlKeyDown = useIsCtrlKeyDown();
  const pageScene = usePageScene();
  const ignoreState =
    isCtrlKeyDown || columnResizing || isDragging || pageScene === PageScene.PAGE_LITE_PREVIEW;

  const block = useRef<HTMLElement | null>(null);
  const start = useRef<[x: number, y: number, scrollTop: number]>();
  const [end, setEnd] = useState<[x: number, y: number, scrollTop: number]>();
  const isInRight = useIsInRight();
  const pageId = useGetPageId();

  // 拖拽选区相关逻辑（保持原有逻辑）
  useEffect(() => {
    if (ignoreState) return;

    const container = querySelectorFromMainContent(
      '.next-space-page',
      isInRight
    ) as HTMLElement | null;

    if (!container) return;

    const handleMouseup = () => {
      start.current = undefined;
      block.current = null;
      setEnd(undefined);
      cancelAnimationFrame(raf.id);
      globalListenerHelper.removeEventListener('mousemove', handleMouseMove);
      globalListenerHelper.removeEventListener('mouseup', handleMouseup);
    };

    const handleMousedown = (event: MouseEvent) => {
      if ($appUiStateCache.$blockMenuListId || event.button !== 0) {
        return;
      }
      if (document.querySelector('[data-modal-id="SETTING"]')) return;

      const target = event.target as HTMLElement;

      const check = checkTargetByHtml(target, (t) => {
        if (
          t.closest(
            '[data-disable-select], [data-draggable], [data-property-id], [data-ignore-draggable]'
          )
        ) {
          return true;
        }
        return false;
      });

      if (check) {
        return;
      }

      const panelName = isInRight ? '.right-panel' : '.main-panel';
      block.current = target.closest(`${panelName} [data-block-id]`);
      start.current = [event.clientX, event.clientY, container.scrollTop];
      globalListenerHelper.addEventListener('mousemove', handleMouseMove);
      globalListenerHelper.addEventListener('mouseup', handleMouseup);
    };

    const handleMouseMove = (event: MouseEvent) => {
      const moveOffset =
        start.current &&
        Math.abs(start.current[0] - event.clientX) < MOVE_OFFSET_NUM &&
        Math.abs(start.current[1] - event.clientY) < MOVE_OFFSET_NUM;

      if (!start.current || moveOffset || cache.ui.selectedCells.length > 0) {
        return;
      }

      if (block.current) {
        const rect = block.current.getBoundingClientRect();
        const id = block.current.dataset.blockId;
        const isTitle = pageId === id;
        const cacheBlock = id && cache.blocks[id];

        let isCollection = false;
        if (cacheBlock && cacheBlock?.type === BlockType.COLLECTION_VIEW) {
          isCollection = true;
        }

        const x1 = rect.left - (isCollection ? HALF_BLOCK_HEIGHT : HORIZONTAL_OFFSET);
        const x2 = rect.right + (isCollection ? HALF_BLOCK_HEIGHT : HORIZONTAL_OFFSET);
        const y1 = rect.top - HALF_BLOCK_HEIGHT;
        const y2 = rect.bottom + HALF_BLOCK_HEIGHT;

        if (
          !isCollection &&
          (isTitle || (event.clientX > x1 && event.clientX < x2)) &&
          event.clientY > y1 &&
          event.clientY < y2
        ) {
          return;
        }
      }

      block.current = null;
      window.getSelection().removeAllRanges();
      document.dispatchEvent(new Event('_clearTextSelection'));

      autoScroll(event.clientX, event.clientY, container);

      setEnd([event.clientX, event.clientY, container.scrollTop]);
    };

    const handleScroll = () => {
      setEnd((prev) => {
        if (!prev) return prev;
        const [x, y] = prev;
        return [x, y, container.scrollTop];
      });
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('mousedown', handleMousedown);
    return () => {
      start.current = undefined;
      cancelAnimationFrame(raf.id);

      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('mousedown', handleMousedown);
      globalListenerHelper.removeEventListener('mouseup', handleMouseup);
      globalListenerHelper.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isInRight, ignoreState, pageId]);

  return useMemo(() => {
    if (!start.current || !end) return;
    const [startX, startY, startTop] = start.current;
    const [endX, endY, endTop] = end;

    const dh = endTop - startTop;
    const sy = startY - dh;
    const left = Math.min(startX, endX);
    const top = Math.min(sy, endY);
    const height = Math.abs(endY - sy);
    const width = Math.abs(endX - startX);

    return { top, left, width, height, startX, startY, endX, endY };
  }, [end]);
};

// ======================== 主组件 ========================

interface SelectorProps {
  type?: 'page' | 'collection';
}

export const Selector: FC<SelectorProps> = ({ type = 'page' }) => {
  const columnResizing = useColumnResizing();
  const isCtrlKeyDown = useIsCtrlKeyDown();
  const ignoreState = isCtrlKeyDown || columnResizing;

  const isInRight = useIsInRight();
  const style = useSelect();
  const blockPosCache = useRef<Map<string, any[]>>(new Map());
  const pageScene = usePageScene();
  const isInMainOrShare = pageScene === PageScene.MAIN || pageScene === PageScene.SHARE;
  const pageId = useGetPageId();

  const unSelectBlock = useCallback(() => {
    if (cache.ui.selectedBlocks.length === 0 && cache.ui.selectedCells.length === 0) return;
    cleanSelectBlock();
  }, []);

  useThrottleEffect(
    () => {
      const run = () => {
        if (!isInMainOrShare) return;
        const root = document.getElementById('root');

        if (!style || ignoreState) {
          setAppUiState({ $isSelecting: false });
          root?.classList.remove('selecting');
          blockPosCache.current = new Map();
          return;
        }

        if (!$appUiStateCache.$isSelecting) {
          setAppUiState({ $isSelecting: true });
          root?.classList.add('selecting');
          blockPosCache.current = new Map();
          blurDocument();
        }

        const { blocks } = cache;
        const hierarchy = new BlockHierarchy(blocks, pageId);

        // 构建选择上下文
        const context = buildSelectionContext(style, hierarchy, isInRight, blockPosCache.current);
        if (!context) {
          unSelectBlock();
          return;
        }

        // 使用选择管理器进行选择
        const manager = new SelectionManager(hierarchy);
        const selectedBlocks = manager.select(context);

        // 转换为最终结果
        const result = convertToSelectBlocks(selectedBlocks, blocks, blockPosCache.current);

        if (!fastEqual(result, cache.ui.selectedBlocks)) {
          dispatch(uiActions.updateSelectBlocks(result));
        }

        // 清理缓存
        hierarchy.clear();
      };

      run();
    },
    [style, ignoreState, type, isInRight, isInMainOrShare, unSelectBlock, pageId],
    { wait: 50, leading: true, trailing: true }
  );

  const reallyLeft = useMemo(() => {
    if (isInRight && style?.left && !isSafari) {
      const mainPanelRight = document
        .querySelector('.main-panel .next-space-page')
        ?.getBoundingClientRect().right;
      if (!mainPanelRight) return undefined;
      return style.left - mainPanelRight;
    }
    return style?.left;
  }, [isInRight, style?.left]);

  if (!isInMainOrShare) return null;

  return (
    <div
      style={{ ...style, left: reallyLeft, zIndex: 1000 }}
      className="fixed bg-active_color opacity-[0.12] pointer-events-none"
    />
  );
};

// ======================== 辅助函数 ========================

function buildSelectionContext(
  style: Rectangle & { startX: number; startY: number; endX: number; endY: number },
  hierarchy: BlockHierarchy,
  isInRight: boolean,
  posCache: Map<string, any[]>
): SelectionContext | null {
  const pageEl = querySelectorFromMainContent(`[data-page-id]`, isInRight) as HTMLElement | null;
  if (!pageEl) return null;

  const firstElementChildRect = pageEl.firstElementChild?.getBoundingClientRect();
  if (!firstElementChildRect) return null;

  const { blocks } = cache;
  const { collectionViews } = getState();
  const listNodes = querySelectorAllFromMainContent(`.block-content [data-block-id]`, isInRight);

  // 构建相交的块信息（优化后的缓存逻辑）
  const intersectingBlocks: BlockInfo[] = [];

  for (const node of listNodes) {
    const { blockId, viewId, groupValue, subGroupValue, syncId, mindMapId } = (node as HTMLElement)
      .dataset;

    if (!blockId || mindMapId) continue;

    const isCalendar = viewId && collectionViews[viewId]?.type === CollectionViewType.CALENDAR;
    const _cache = posCache.get(blockId);

    if (!_cache || isCalendar || groupValue) {
      const rect = node.getBoundingClientRect();
      const blockInfo = {
        left: rect.left,
        width: rect.width,
        height: rect.height,
        offsetTop: rect.top - firstElementChildRect.top,
        viewId,
        groupValue,
        subGroupValue,
        syncId,
      };

      if (_cache) {
        posCache.set(blockId, uniqWith(_cache.concat(blockInfo), fastEqual));
      } else {
        posCache.set(blockId, [blockInfo]);
      }
    }

    // 检查是否与选区相交
    const cachedBlockInfo = posCache.get(blockId);
    if (!cachedBlockInfo) continue;

    let intersect = false;
    let blockRect: Rectangle | undefined;

    cachedBlockInfo.forEach((item, index) => {
      const rect = {
        left: item.left,
        top: firstElementChildRect.top + item.offsetTop,
        width: item.width,
        height: item.height,
      };

      if (index === 0) {
        blockRect = rect;
      }

      if (isIntersect(rect, style)) {
        intersect = true;
      }
    });

    if (intersect && blockRect) {
      const block = blocks[blockId];
      if (!block || block.type === BlockType.COLUMN_LIST) continue;

      const parent = blocks[block.parentId];
      if (!parent) continue;

      const blockInfo = {
        blockId,
        block,
        rect: blockRect,
        depth: hierarchy.getDepth(blockId),
        parentId: block.parentId,
        ancestors: hierarchy.getAncestors(blockId),
      };

      intersectingBlocks.push(blockInfo);
    }
  }

  // 构建选择点
  const startBlockId = DOMInteraction.getBlockAtPoint(style.startX, style.startY);
  const endBlockId = DOMInteraction.getBlockAtPoint(style.endX, style.endY);

  const startPoint: SelectionPoint = {
    x: style.startX,
    y: style.startY,
    blockId: startBlockId || undefined,
    blockInfo: startBlockId
      ? DOMInteraction.getBlockInfo(startBlockId, intersectingBlocks)
      : undefined,
  };

  const endPoint: SelectionPoint = {
    x: style.endX,
    y: style.endY,
    blockId: endBlockId || undefined,
    blockInfo: endBlockId ? DOMInteraction.getBlockInfo(endBlockId, intersectingBlocks) : undefined,
  };

  return {
    startPoint,
    endPoint,
    intersectingBlocks,
    style,
  };
}

function convertToSelectBlocks(
  blockInfos: BlockInfo[],
  blocks: typeof cache.blocks,
  posCache: Map<string, any[]>
): SelectBlock[] {
  const result: SelectBlock[] = [];
  const blockIdsOrder: string[] = [];

  // 按照垂直位置排序
  const sortedBlocks = [...blockInfos].sort((a, b) => a.rect.top - b.rect.top);

  for (const blockInfo of sortedBlocks) {
    const block = blocks[blockInfo.blockId];
    const parentId = block?.parentId;

    if (parentId) {
      const parent = blocks[parentId];
      if (parent && isCollection(parent.type)) {
        const { role } = getPermissions(blockInfo.blockId);
        if (role === PermissionRole.READER) continue;
      }
    }

    blockIdsOrder.push(blockInfo.blockId);
  }

  // 构建最终结果
  for (const blockId of blockIdsOrder) {
    const blockInfo = blockInfos.find((b) => b.blockId === blockId);
    if (!blockInfo) continue;

    const block = blocks[blockId];
    if (!block) continue;

    // 从缓存中获取viewId和syncId
    const cachedBlockInfo = posCache.get(blockId);
    const firstRect = cachedBlockInfo?.[0];

    const selectBlock: SelectBlock = {
      blockId,
      viewId: firstRect?.viewId,
      syncId: firstRect?.syncId,
    };

    // 处理groupValues
    if (firstRect?.groupValue) {
      const allGroupValues: Record<string, string[]> = {};

      cachedBlockInfo?.forEach((item) => {
        const { groupValue } = item;
        if (groupValue) {
          const oldValues = allGroupValues[groupValue ?? ''] ?? [];
          allGroupValues[groupValue] = item.subGroupValue
            ? oldValues.concat(item.subGroupValue)
            : oldValues;
        }
      });

      selectBlock.groupValues = allGroupValues;
    }

    result.push(selectBlock);
  }

  return result;
}
